import { CustomerContactPersonModelType } from '@teinor/erp/types/company/customer/customerContactPerson'
import { MediumModalCreateEdit } from '../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { ModalEditComponentProps } from '../../../../../baseComponents/ModalsLayouts/types'
import { FormRender } from '../../../../../forms/FormRenderer/types'
import { TwinDictionary } from '../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { returnClassOfSpanTemplate } from '../../../../../utils/globals/tailwind'
import { CustomSelectAndCreateSectionCustomer } from '../../../CustomSelectSectionCustomers'
import { CustomerPayload } from '../../../../../utils/reducers/customers/customer'
import { AllReduxPayloads } from '../../../../../utils/reducers'
import { connect, ConnectedProps } from 'react-redux'
import withLoading from '../../../../../utils/hoc/withLoading'
import useEditDeleteModal from '../../../../../utils/hooks/useEditDeleteModal'
import { useCallback, useState } from 'react'
import { ModalCreateCustomerOutside } from '../../../../../app/erp/screens/Company/CustomerParent/AllCustomers/ModalCreateCustomer'
import { OnSubmit } from '../../../../../forms/TwinForm/types'

interface CustomerContactPersonModelTypeExtends extends CustomerContactPersonModelType {
    customerContactPersonAccesses: string[]
}

type ModalCreateCustomerContactPersonProps = & ReduxCustomerContacts & ModalEditComponentProps & { }

const ModalCreateCustomerContactPerson: React.FC<ModalCreateCustomerContactPersonProps> = ({ userPermissions, allRowData, customers, basicData, ...rest }) => {
    const { t } = useTwinTranslation()
    const { openModal, setOpenModal, myCustomerOnChange, myCustomerOnSubmit, selectedCustomerId } = useModalCreateCustomerContactPersonLogic()
    const accesses: TwinDictionary = { 'task_bag_of_hours': { value: 'task_bag_of_hours', name: t('bagOfHours', 'Bolsa de horas') } }

    const fields: FormRender<CustomerContactPersonModelTypeExtends> = [
        {
            cols: 3,
            elements: [
                {
                    name: 'name',
                    label: t('name', 'Nombre'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    required: true
                },
                {
                    name: 'position',
                    label: t('position', 'Cargo'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
                {
                    name: 'notes',
                    label: t('notes', 'Notas'),
                    component: 'TextArea',
                    parentClassName: returnClassOfSpanTemplate(3)
                },
                {
                    name: 'id',
                    component: 'InputHidden'
                },
            ],
        },
        {
            cols: 3,
            title: t('contatInformation', 'Información de contacto'),
            elements: [
                {
                    name: 'phone',
                    label: t('phone', 'Teléfono'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'mobile',
                    label: t('mobile', 'Móvil'),
                    component: 'InputWithLabelMargin',
                    type: 'text'
                },
                {
                    name: 'email',
                    label: t('email', 'Email'),
                    component: 'InputWithLabelMargin',
                    type: 'email',
                },
            ],
        },
        {
            cols: 1,
            elements: [
                {
                    name: 'customerContactPersonAccesses',
                    component: 'MultipleCheckboxSelector',
                    subtitles: {
                        subtitleOptions: t('accesses', 'Accesos'),
                        subtitleSelectedOptions: t('selectedAccesses', 'Accesos seleccionados')
                    },
                    items: accesses || {},
                },
            ]
        }
    ]
    if (basicData?.CustomerId) {
        fields[0].elements.push({
            name: 'CustomerId',
            component: 'InputHidden',
            value: basicData?.CustomerId
        })
    } else {
        fields[0].elements.unshift({
            name: 'CustomerId',
            component: 'Custom',
            render: ({ extraProps }) => {
                return <CustomSelectAndCreateSectionCustomer name='CustomerId' customers={extraProps.customers} label={t('customer', 'Cliente')} required={true} value={selectedCustomerId || ''} onChange={myCustomerOnChange} />
            },
            extraProps: { customers }
        })
    }
    return (<MediumModalCreateEdit fields={fields} url={'/api/app/customer/customerContactPerson/createCustomerContactPerson'} translations={{
        title: t('createContactPerson', 'Crear Persona de contacto'),
        button: t('create', 'Crear')
    }} haveButtonPermissions={true} {...rest}>
        {openModal?.type === 'create' && <ModalCreateCustomerOutside setOpened={() => setOpenModal(null)} onSubmit={myCustomerOnSubmit} />}
        </MediumModalCreateEdit>
    )
}

const useModalCreateCustomerContactPersonLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null)

    const myCustomerOnChange = useCallback((value: number) => {
        if (value === 0) {
            setOpenModal({ type: 'create', allRowData: {} })
        } else if (value) {
            setSelectedCustomerId(value)
        }
    }, [setOpenModal, setSelectedCustomerId])

    const myCustomerOnSubmit: OnSubmit = useCallback(async (e: any) => {
        const result = await e.json()
        if (result) {
            setSelectedCustomerId(result?.id)
            setOpenModal(null)
        }
    }, [setOpenModal, setSelectedCustomerId])

    return { openModal, setOpenModal, myCustomerOnChange, myCustomerOnSubmit, selectedCustomerId }
}


const modalCreateCustomerContactsDispatch = {
    setCustomers: (payload: CustomerPayload) => ({ type: 'CHANGE_CUSTOMER', payload }),
}

export type ReduxCustomerContacts = ConnectedProps<typeof customerContactsConnect>
const mapCustomerContactsTypesConnector = (state: AllReduxPayloads) => ({ customers: state.customers })
const customerContactsConnect = connect(mapCustomerContactsTypesConnector, modalCreateCustomerContactsDispatch)

const customerContactsConnectLoading = withLoading(ModalCreateCustomerContactPerson, [{ fetchUrl: '/api/app/customer/getAllCustomersComplete', propName: 'customers', setFunctionName: 'setCustomers' }])

const ModalCreateCustomerContactPersonConnect = customerContactsConnect(customerContactsConnectLoading)

export default ModalCreateCustomerContactPersonConnect